import React, { useState, useEffect } from 'react';
import api from '../services/api';

// ═══ Hook responsive ═══
const useResponsive = () => {
    const getState = () => {
        const w = window.innerWidth;
        return { isSmall: w <= 480, isMobile: w <= 900 };
    };
    const [state, setState] = useState(getState);
    useEffect(() => {
        const handleResize = () => setState(getState());
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    return state;
};

// ═══════════════════════════════════════════════════════════
// AdminAbonnements — à placer dans src/components/
// S'intègre dans la page Admin existante (onglet "Abonnements").
// Reprend les styles de Admin.js (cartes blanches, tableau bordeaux).
// ═══════════════════════════════════════════════════════════

const AdminAbonnements = () => {
    const { isSmall, isMobile } = useResponsive();
    const [plans, setPlans] = useState([]);
    const [regles, setRegles] = useState({ homme_requis: true, femme_requis: false });
    const [membres, setMembres] = useState([]);
    // Filtres et pagination des souscriptions
    const [sRecherche, setSRecherche] = useState('');
    const [sStatut, setSStatut] = useState('');    // '', 'actif', 'expire'
    const [sGenre, setSGenre] = useState('');
    const [sFormule, setSFormule] = useState('');
    const [sPage, setSPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(null);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [edits, setEdits] = useState({}); // { code: { libelle, prix } }

    useEffect(() => {
        fetchParametres();
        fetchMembres();
    }, []);

    useEffect(() => {
        if (message || error) {
            const t = setTimeout(() => { setMessage(''); setError(''); }, 4000);
            return () => clearTimeout(t);
        }
    }, [message, error]);

    const fetchParametres = async () => {
        try {
            const response = await api.get('/admin/abonnements/parametres');
            setPlans(response.data.plans);
            setRegles(response.data.regles);
            // Pré-remplit les champs d'édition
            const init = {};
            response.data.plans.forEach(p => {
                init[p.code] = { libelle: p.libelle, prix: String(p.prix) };
            });
            setEdits(init);
        } catch (err) {
            console.error(err);
            setError('Impossible de charger les paramètres');
        } finally {
            setLoading(false);
        }
    };

    const fetchMembres = async () => {
        try {
            const response = await api.get('/admin/abonnements/membres');
            setMembres(response.data);
        } catch (err) {
            console.error(err);
        }
    };

    const handleEdit = (code, champ, valeur) => {
        setEdits(prev => ({
            ...prev,
            [code]: { ...prev[code], [champ]: valeur },
        }));
    };

    const handleSaveTarif = async (code) => {
        setSaving(code);
        setError('');
        try {
            const { libelle, prix } = edits[code];
            await api.put(`/admin/abonnements/tarif/${code}`, {
                libelle,
                prix: parseFloat(String(prix).replace(',', '.')),
            });
            setMessage(`Formule "${libelle}" mise à jour`);
            fetchParametres();
        } catch (err) {
            setError(err.response?.data?.message || 'Erreur lors de la mise à jour');
        } finally {
            setSaving(null);
        }
    };

    const handleToggleActif = async (code, actif) => {
        setSaving(code);
        try {
            await api.put(`/admin/abonnements/tarif/${code}`, { actif: !actif });
            fetchParametres();
        } catch (err) {
            setError(err.response?.data?.message || 'Erreur');
        } finally {
            setSaving(null);
        }
    };

    const handleSaveRegles = async (nouvelles) => {
        setSaving('regles');
        setError('');
        try {
            await api.put('/admin/abonnements/regles', nouvelles);
            setRegles(nouvelles);
            setMessage('Règles d\'abonnement mises à jour');
        } catch (err) {
            setError(err.response?.data?.message || 'Erreur lors de la mise à jour');
        } finally {
            setSaving(null);
        }
    };

    // ═══ Filtrage et pagination des souscriptions ═══
    const PAR_PAGE = 10;

    const estActif = (m) => m.statut === 'actif' && new Date(m.date_fin) > new Date();

    const membresFiltres = membres.filter(m => {
        if (sStatut === 'actif' && !estActif(m)) return false;
        if (sStatut === 'expire' && estActif(m)) return false;
        if (sGenre && m.gender !== sGenre) return false;
        if (sFormule && m.code !== sFormule) return false;
        if (sRecherche.trim()) {
            const q = sRecherche.trim().toLowerCase();
            const champs = [m.first_name, m.email].filter(Boolean).join(' ').toLowerCase();
            if (!champs.includes(q)) return false;
        }
        return true;
    });

    const sTotalPages = Math.max(1, Math.ceil(membresFiltres.length / PAR_PAGE));
    const sPageCourante = Math.min(sPage, sTotalPages);
    const membresAffiches = membresFiltres.slice(
        (sPageCourante - 1) * PAR_PAGE,
        sPageCourante * PAR_PAGE
    );

    const formulesDisponibles = [...new Set(membres.map(m => m.code).filter(Boolean))].sort();
    const sFiltresActifs = [sRecherche, sStatut, sGenre, sFormule].filter(Boolean).length;
    const resetFiltresSouscriptions = () => {
        setSRecherche(''); setSStatut(''); setSGenre(''); setSFormule(''); setSPage(1);
    };

    const formatDate = (d) => new Date(d).toLocaleDateString('fr-FR');
    const formatPrix = (p, devise) => {
        const n = parseFloat(p);
        if (n === 0) return 'Gratuit';
        return `${n.toFixed(2).replace('.', ',')} ${devise === 'EUR' ? '€' : devise}`;
    };

    // Statistiques rapides
    const actifs = membres.filter(m => m.statut === 'actif' && new Date(m.date_fin) > new Date());
    const revenus = membres
        .filter(m => m.statut !== 'annule')
        .reduce((sum, m) => sum + parseFloat(m.prix_paye || 0), 0);

    if (loading) {
        return <div style={styles.loading}>Chargement des paramètres...</div>;
    }

    return (
        <div>
            <h2 style={{ ...styles.title, fontSize: isSmall ? '23px' : isMobile ? '26px' : '30px', textAlign: isMobile ? 'center' : 'left' }}>Gestion des abonnements</h2>

            {message && <div style={styles.success}>{message}</div>}
            {error && <div style={styles.error}>{error}</div>}

            {/* ═══ STATISTIQUES ═══ */}
            <div style={{ ...styles.statsGrid, gridTemplateColumns: isSmall ? '1fr 1fr' : 'repeat(auto-fit, minmax(190px, 1fr))', gap: isSmall ? '12px' : '20px' }}>
                <div style={styles.statCard}>
                    <div style={styles.statNumber}>{actifs.length}</div>
                    <div style={styles.statLabel}>Abonnements actifs</div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statNumber}>{membres.length}</div>
                    <div style={styles.statLabel}>Total souscriptions</div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statNumber}>{revenus.toFixed(2).replace('.', ',')} €</div>
                    <div style={styles.statLabel}>Revenus cumulés</div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statNumber}>
                        {actifs.filter(m => m.gender === 'homme').length} / {actifs.filter(m => m.gender === 'femme').length}
                    </div>
                    <div style={styles.statLabel}>Hommes / Femmes abonnés</div>
                </div>
            </div>

            {/* ═══ RÈGLES ═══ */}
            <div style={styles.section}>
                <h3 style={{ ...styles.sectionTitle, textAlign: isMobile ? 'center' : 'left' }}>Qui doit s'abonner ?</h3>
                <p style={styles.sectionDesc}>
                    Définissez si l'abonnement est obligatoire pour accéder aux fonctionnalités complètes.
                    Un membre sans abonnement requis conserve un accès gratuit à l'annuaire.
                </p>

                <div style={styles.reglesRow}>
                    <div style={styles.regleCard}>
                        <div style={styles.regleHead}>
                            <span style={styles.regleIcon}>♂</span>
                            <span style={styles.regleName}>Hommes</span>
                        </div>
                        <label style={styles.switchRow}>
                            <input
                                type="checkbox"
                                checked={regles.homme_requis}
                                onChange={(e) => handleSaveRegles({ ...regles, homme_requis: e.target.checked })}
                                disabled={saving === 'regles'}
                                style={styles.checkbox}
                            />
                            <span style={styles.switchLabel}>
                                {regles.homme_requis ? 'Abonnement obligatoire' : 'Accès gratuit'}
                            </span>
                        </label>
                    </div>

                    <div style={styles.regleCard}>
                        <div style={styles.regleHead}>
                            <span style={styles.regleIcon}>♀</span>
                            <span style={styles.regleName}>Femmes</span>
                        </div>
                        <label style={styles.switchRow}>
                            <input
                                type="checkbox"
                                checked={regles.femme_requis}
                                onChange={(e) => handleSaveRegles({ ...regles, femme_requis: e.target.checked })}
                                disabled={saving === 'regles'}
                                style={styles.checkbox}
                            />
                            <span style={styles.switchLabel}>
                                {regles.femme_requis ? 'Abonnement obligatoire' : 'Accès gratuit'}
                            </span>
                        </label>
                    </div>
                </div>

                <div style={styles.warningBox}>
                    ⚠️ Activer l'abonnement obligatoire pour les femmes bloquera immédiatement
                    l'accès aux fiches détaillées pour celles qui n'ont pas souscrit.
                </div>
            </div>

            {/* ═══ TARIFS ═══ */}
            <div style={styles.section}>
                <h3 style={{ ...styles.sectionTitle, textAlign: isMobile ? 'center' : 'left' }}>Tarifs des formules</h3>
                <p style={styles.sectionDesc}>
                    Modifiez le libellé et le prix de chaque formule. Les changements
                    s'appliquent immédiatement aux nouvelles souscriptions.
                </p>

                <div style={styles.tableScroll}><table style={styles.table}>
                    <thead>
                        <tr style={styles.tableHeader}>
                            <th style={styles.th}>Code</th>
                            <th style={styles.th}>Libellé</th>
                            <th style={styles.th}>Durée</th>
                            <th style={styles.th}>Prix (€)</th>
                            <th style={styles.th}>Statut</th>
                            <th style={styles.th}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {plans.map(plan => (
                            <tr key={plan.code} style={styles.tableRow}>
                                <td style={styles.td}>
                                    <code style={styles.code}>{plan.code}</code>
                                </td>
                                <td style={styles.td}>
                                    <input
                                        value={edits[plan.code]?.libelle || ''}
                                        onChange={(e) => handleEdit(plan.code, 'libelle', e.target.value)}
                                        style={styles.input}
                                    />
                                </td>
                                <td style={styles.td}>
                                    {plan.duree_mois === 0 ? '—' : `${plan.duree_mois} mois`}
                                </td>
                                <td style={styles.td}>
                                    <input
                                        type="text"
                                        value={edits[plan.code]?.prix ?? ''}
                                        onChange={(e) => handleEdit(plan.code, 'prix', e.target.value)}
                                        disabled={plan.code === 'gratuit'}
                                        style={{ ...styles.input, width: '90px', textAlign: 'right' }}
                                    />
                                </td>
                                <td style={styles.td}>
                                    <span style={plan.actif ? styles.actif : styles.inactif}>
                                        {plan.actif ? '✅ Active' : '⛔ Masquée'}
                                    </span>
                                </td>
                                <td style={styles.td}>
                                    <button
                                        onClick={() => handleSaveTarif(plan.code)}
                                        disabled={saving === plan.code}
                                        style={styles.saveBtn}
                                    >
                                        {saving === plan.code ? '...' : 'Enregistrer'}
                                    </button>
                                    {plan.code !== 'gratuit' && (
                                        <button
                                            onClick={() => handleToggleActif(plan.code, plan.actif)}
                                            disabled={saving === plan.code}
                                            style={plan.actif ? styles.hideBtn : styles.showBtn}
                                        >
                                            {plan.actif ? 'Masquer' : 'Afficher'}
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table></div>
            </div>

            {/* ═══ MEMBRES ABONNÉS ═══ */}
            <div style={styles.section}>
                <h3 style={{ ...styles.sectionTitle, textAlign: isMobile ? 'center' : 'left' }}>Souscriptions</h3>
                {/* ═══ Filtres, tableau et pagination dans un bloc unique ═══ */}
                <div style={styles.blocTableau}>
                    {membres.length > 0 && (
                        <div style={styles.filtresCard}>
                            <div style={{ ...styles.filtresRow, flexDirection: isSmall ? 'column' : 'row', alignItems: isSmall ? 'stretch' : 'flex-end', gap: isSmall ? '12px' : '14px' }}>
                                <label style={{ ...styles.filtreLabel, flex: isSmall ? '0 0 auto' : '1 1 160px' }}>
                                    Rechercher
                                    <input
                                        value={sRecherche}
                                        onChange={e => { setSRecherche(e.target.value); setSPage(1); }}
                                        placeholder="Nom ou email"
                                        style={styles.filtreInput}
                                    />
                                </label>
                                <label style={{ ...styles.filtreLabel, flex: isSmall ? '0 0 auto' : '1 1 160px' }}>
                                    Statut
                                    <select value={sStatut} onChange={e => { setSStatut(e.target.value); setSPage(1); }} style={styles.filtreInput}>
                                        <option value="">Tous</option>
                                        <option value="actif">Actifs</option>
                                        <option value="expire">Expirés</option>
                                    </select>
                                </label>
                                <label style={{ ...styles.filtreLabel, flex: isSmall ? '0 0 auto' : '1 1 160px' }}>
                                    Genre
                                    <select value={sGenre} onChange={e => { setSGenre(e.target.value); setSPage(1); }} style={styles.filtreInput}>
                                        <option value="">Tous</option>
                                        <option value="homme">Hommes</option>
                                        <option value="femme">Femmes</option>
                                    </select>
                                </label>
                                <label style={{ ...styles.filtreLabel, flex: isSmall ? '0 0 auto' : '1 1 160px' }}>
                                    Formule
                                    <select value={sFormule} onChange={e => { setSFormule(e.target.value); setSPage(1); }} style={styles.filtreInput}>
                                        <option value="">Toutes</option>
                                        {formulesDisponibles.map(f => <option key={f} value={f}>{f}</option>)}
                                    </select>
                                </label>
                                {sFiltresActifs > 0 && (
                                    <button onClick={resetFiltresSouscriptions} style={styles.resetFiltreBtn}>
                                        Réinitialiser
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {membres.length === 0 ? (
                        <div style={styles.empty}>Aucune souscription pour le moment.</div>
                    ) : membresFiltres.length === 0 ? (
                        <div style={styles.empty}>Aucune souscription ne correspond à ces critères.</div>
                    ) : (
                        <>
                            <div style={styles.tableScroll}><table style={styles.table}>
                                <thead>
                                    <tr style={styles.tableHeader}>
                                        <th style={styles.th}>Membre</th>
                                        <th style={styles.th}>Genre</th>
                                        <th style={styles.th}>Formule</th>
                                        <th style={styles.th}>Montant</th>
                                        <th style={styles.th}>Début</th>
                                        <th style={styles.th}>Fin</th>
                                        <th style={styles.th}>Statut</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {membresAffiches.map(m => {
                                        const encoreActif = m.statut === 'actif' && new Date(m.date_fin) > new Date();
                                        return (
                                            <tr key={m.id} style={styles.tableRow}>
                                                <td style={styles.td}>
                                                    <strong>{m.first_name || '—'}</strong>
                                                    <br />
                                                    <span style={{ fontSize: '12px', color: '#999' }}>{m.email}</span>
                                                </td>
                                                <td style={styles.td}>{m.gender}</td>
                                                <td style={styles.td}>{m.code}</td>
                                                <td style={styles.td}>{formatPrix(m.prix_paye, m.devise)}</td>
                                                <td style={styles.td}>{formatDate(m.date_debut)}</td>
                                                <td style={styles.td}>{formatDate(m.date_fin)}</td>
                                                <td style={styles.td}>
                                                    <span style={encoreActif ? styles.actif : styles.inactif}>
                                                        {encoreActif ? '✅ Actif' : '⏳ Expiré'}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table></div>

                            {/* ═══ Pagination ═══ */}
                            <div style={styles.pagination}>
                                <span style={styles.paginationInfo}>
                                    {membresFiltres.length} souscription{membresFiltres.length > 1 ? 's' : ''}
                                    {sFiltresActifs > 0 ? ' (filtrées)' : ''}
                                    {' · '}page {sPageCourante} sur {sTotalPages}
                                </span>
                                {sTotalPages > 1 && (
                                    <div style={styles.paginationBtns}>
                                        <button
                                            onClick={() => setSPage(p => Math.max(1, p - 1))}
                                            disabled={sPageCourante === 1}
                                            style={sPageCourante === 1 ? styles.pageBtnDisabled : styles.pageBtn}
                                        >
                                            ‹ Précédent
                                        </button>
                                        <button
                                            onClick={() => setSPage(p => Math.min(sTotalPages, p + 1))}
                                            disabled={sPageCourante === sTotalPages}
                                            style={sPageCourante === sTotalPages ? styles.pageBtnDisabled : styles.pageBtn}
                                        >
                                            Suivant ›
                                        </button>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

const styles = {
    title: { color: '#3D0F1E', marginBottom: '24px', fontSize: '30px', fontWeight: '700', fontFamily: "'Cormorant Garamond', Georgia, serif", letterSpacing: '0.3px', wordBreak: 'break-word' },

    statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '24px', marginBottom: '32px' },
    statCard: { backgroundColor: 'white', minWidth: 0, padding: '26px 18px', borderRadius: '12px', textAlign: 'center', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', border: '1px solid #EADCE2', minHeight: '140px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px', boxSizing: 'border-box' },
    statNumber: { fontSize: '34px', fontWeight: '700', color: '#3D0F1E', fontFamily: "'Cormorant Garamond', Georgia, serif", lineHeight: 1.1, whiteSpace: 'nowrap' },
    statLabel: { fontSize: '14px', color: '#888', marginTop: '8px' },

    section: { backgroundColor: 'white', padding: '28px', borderRadius: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', border: '1px solid #EADCE2', marginBottom: '24px' },
    sectionTitle: { color: '#3D0F1E', fontSize: '22px', margin: '0 0 8px', fontWeight: '700', fontFamily: "'Cormorant Garamond', Georgia, serif" },
    sectionDesc: { color: '#888', fontSize: '13.5px', lineHeight: '1.6', margin: '0 0 20px' },

    reglesRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '18px' },
    regleCard: { border: '1px solid #EADCE2', borderRadius: '10px', padding: '18px 20px', backgroundColor: '#FBF6F1' },
    regleHead: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' },
    regleIcon: { fontSize: '22px', color: '#3D0F1E', fontWeight: '700' },
    regleName: { color: '#3D0F1E', fontSize: '16px', fontWeight: '700' },
    switchRow: { display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' },
    checkbox: { width: '18px', height: '18px', cursor: 'pointer', accentColor: '#6B1E35' },
    switchLabel: { color: '#444', fontSize: '14px', fontWeight: '600' },

    warningBox: { marginTop: '18px', padding: '12px 16px', backgroundColor: '#fff8e6', border: '1px solid #C4688A', borderRadius: '8px', color: '#7a5c00', fontSize: '13px', lineHeight: '1.5' },

    // Bloc unique : filtres, tableau et pagination dans une seule carte
    blocTableau: { backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.06)', border: '1px solid #EADCE2', overflow: 'hidden' },
    filtresCard: { padding: '18px 20px', borderBottom: '1px solid #F0E4E9' },
    filtresRow: { display: 'flex', alignItems: 'flex-end', gap: '14px', flexWrap: 'wrap' },
    filtreLabel: { display: 'flex', flexDirection: 'column', gap: '6px', color: '#6B1E35', fontSize: '12.5px', fontWeight: '700', flex: '1 1 160px', minWidth: 0 },
    filtreInput: { padding: '9px 11px', border: '1.5px solid #EADCE2', borderRadius: '8px', fontSize: '13.5px', color: '#444', outline: 'none', width: '100%', boxSizing: 'border-box' },
    resetFiltreBtn: { padding: '10px 18px', backgroundColor: '#fff', color: '#6B1E35', border: '1.5px solid #EADCE2', borderRadius: '8px', fontWeight: '700', fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap' },
    pagination: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '14px', flexWrap: 'wrap', padding: '14px 20px', borderTop: '1px solid #F0E4E9' },
    paginationInfo: { color: '#8A8A8A', fontSize: '13px' },
    paginationBtns: { display: 'flex', gap: '8px' },
    pageBtn: { padding: '8px 16px', backgroundColor: '#fff', color: '#6B1E35', border: '1.5px solid #EADCE2', borderRadius: '8px', fontWeight: '700', fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap' },
    pageBtnDisabled: { padding: '8px 16px', backgroundColor: '#F5F5F7', color: '#BBB', border: '1.5px solid #EEE', borderRadius: '8px', fontWeight: '700', fontSize: '13px', cursor: 'not-allowed', whiteSpace: 'nowrap' },
    tableScroll: { width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch', padding: '6px 20px 0', boxSizing: 'border-box' },
    table: { width: '100%', minWidth: '660px', borderCollapse: 'separate', borderSpacing: 0, backgroundColor: 'white', borderRadius: '10px', overflow: 'hidden' },
    tableHeader: { backgroundColor: '#3D0F1E' },
    th: { padding: '14px 16px', color: '#C4688A', textAlign: 'left', fontWeight: '600', fontSize: '14px' },
    tableRow: {},
    td: { padding: '12px 16px', color: '#444', fontSize: '14px', borderBottom: '1px solid #F0E4E9' },
    code: { backgroundColor: '#FBF6F1', padding: '3px 8px', borderRadius: '4px', fontSize: '12px', color: '#6B1E35', fontWeight: '600' },
    input: { padding: '8px 10px', border: '1.5px solid #EADCE2', borderRadius: '6px', fontSize: '14px', outline: 'none', color: '#444', width: '100%', boxSizing: 'border-box' },

    actif: { color: '#5A7A4E', fontWeight: '600' },
    inactif: { color: '#999', fontWeight: '600' },

    saveBtn: { padding: '6px 14px', backgroundColor: '#5A7A4E', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: '8px', fontWeight: '600', fontSize: '13px' },
    hideBtn: { padding: '6px 14px', backgroundColor: '#999', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' },
    showBtn: { padding: '6px 14px', backgroundColor: '#6B1E35', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' },

    empty: { textAlign: 'center', color: '#888', fontSize: '15px', padding: '32px', fontStyle: 'italic' },
    loading: { textAlign: 'center', color: '#3D0F1E', fontSize: '18px', padding: '40px' },
    success: { backgroundColor: '#f0fff0', color: '#5A7A4E', padding: '12px', borderRadius: '8px', marginBottom: '16px', textAlign: 'center', border: '1px solid #5A7A4E' },
    error: { backgroundColor: '#ffe0e0', color: '#c00', padding: '12px', borderRadius: '8px', marginBottom: '16px', textAlign: 'center' },
};

export default AdminAbonnements;